/**
 * useTelzioSoftphone — JsSIP wrapper for Telzio's browser softphone.
 *
 * Lifecycle:
 *  1. Caller invokes `connect()` which fetches /api/telzio/sip/parameters,
 *     constructs a JsSIP UA, and starts it (auto-registers).
 *  2. Outbound calls via `call(targetE164)`. Inbound calls surface via
 *     `incomingSession` state — caller decides to `answer()` / `reject()`.
 *  3. `disconnect()` stops the UA and cleans up media.
 *
 * State exposed:
 *  - phase: 'idle' | 'connecting' | 'registered' | 'in-call' | 'ringing' | 'error'
 *  - error: string | null
 *  - currentCall: { direction, peerLabel, peerE164, startedAt, durationSec, muted }
 *  - incomingSession: jssip session | null (for ringing modal)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import JsSIP from 'jssip';
import { authFetch } from '../lib/api';

// Enable JsSIP's verbose SIP-traffic logging so we can diagnose registration
// problems from the browser console without asking users to set localStorage.
// Safe to leave on; it only prints to console.
try { JsSIP.debug.enable('JsSIP:*'); } catch { /* noop */ }

const API = process.env.REACT_APP_BACKEND_URL;

// Strip any non-digit chars except leading +
const normalizeE164 = (n) => {
  if (!n) return null;
  const s = String(n).replace(/[^\d+]/g, '');
  if (s.startsWith('+')) return s;
  if (s.length === 10) return `+1${s}`; // assume US fallback
  if (s.length >= 11) return `+${s}`;
  return s;
};

export default function useTelzioSoftphone() {
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [incomingSession, setIncomingSession] = useState(null);
  const [muted, setMuted] = useState(false);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioElRef = useRef(null);
  const tickRef = useRef(null);

  // Lazy-create the hidden <audio> sink
  useEffect(() => {
    if (!audioElRef.current) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.setAttribute('data-testid', 'softphone-audio-sink');
      el.style.display = 'none';
      document.body.appendChild(el);
      audioElRef.current = el;
    }
    return () => {
      if (audioElRef.current) {
        audioElRef.current.remove();
        audioElRef.current = null;
      }
    };
  }, []);

  const cleanupSession = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    sessionRef.current = null;
    setCurrentCall(null);
    setIncomingSession(null);
    setMuted(false);
    if (uaRef.current && uaRef.current.isRegistered()) {
      setPhase('registered');
    }
  }, []);

  const wireSession = useCallback((session, direction, peerLabel, peerE164) => {
    sessionRef.current = session;

    session.on('peerconnection', (e) => {
      const pc = e.peerconnection;
      pc.addEventListener('track', (ev) => {
        if (audioElRef.current && ev.streams && ev.streams[0]) {
          audioElRef.current.srcObject = ev.streams[0];
        }
      });
    });

    session.on('progress', () => {
      setPhase('ringing');
    });

    session.on('accepted', () => {
      const startedAt = Date.now();
      setPhase('in-call');
      setCurrentCall({ direction, peerLabel, peerE164, startedAt, durationSec: 0, muted: false });
      tickRef.current = setInterval(() => {
        setCurrentCall((c) => (c ? { ...c, durationSec: Math.floor((Date.now() - c.startedAt) / 1000) } : c));
      }, 1000);
    });

    session.on('confirmed', () => {
      // SDP answer fully exchanged
    });

    session.on('ended', cleanupSession);
    session.on('failed', (e) => {
      setError(e?.cause || 'Call failed');
      cleanupSession();
    });
  }, [cleanupSession]);

  const connect = useCallback(async () => {
    if (uaRef.current) return;
    setError(null);
    setPhase('connecting');

    let params;
    try {
      const res = await authFetch(`${API}/api/telzio/sip/parameters`);
      if (!res.ok) throw new Error(`Failed to fetch SIP params (${res.status})`);
      params = await res.json();
      // eslint-disable-next-line no-console
      console.info('[Softphone] SIP params keys:', Object.keys(params || {}), params);
    } catch (e) {
      setError(e.message);
      setPhase('error');
      return;
    }

    try {
      const sipUri = params.sip_uri;
      const wsEndpoints = params.ws_sip_endpoints || [];
      if (!sipUri || wsEndpoints.length === 0) {
        throw new Error('Missing sip_uri or ws_sip_endpoints in Telzio response');
      }

      // Telzio's /sip/parameters doesn't return a SIP password — instead the
      // user's bearer claims_token is used (same pattern as Telzio mobile apps).
      // Backend injects it as `bearer_token` for us.
      const password = params.bearer_token || params.sip_password || params.password || params.token || null;
      const authUser = params.sip_username || params.auth_user || sipUri.split('@')[0].replace('sip:', '');

      // Telzio returns bare hostnames (e.g. "mobile.sip.telzio.com"); JsSIP
      // requires full ws:// or wss:// URLs.
      const sockets = wsEndpoints.map((endpoint) => {
        const fullUrl = /^wss?:\/\//.test(endpoint) ? endpoint : `wss://${endpoint}`;
        return new JsSIP.WebSocketInterface(fullUrl);
      });

      const pcConfig = {};
      const wpcd = params.web_rtc_peer_connection_data || {};
      const iceServers = [];
      (wpcd.stun_servers || []).forEach((urls) => iceServers.push({ urls }));
      const turnGroups = wpcd.turn_servers || {};
      ['turn_udp', 'turn_tls', 'turn_tls_ga'].forEach((k) => {
        const t = turnGroups[k];
        if (t && t.urls) iceServers.push({ urls: t.urls, username: t.username, credential: t.password });
      });
      if (iceServers.length) pcConfig.iceServers = iceServers;
      if (wpcd.bundle_policy) pcConfig.bundlePolicy = wpcd.bundle_policy;
      if (typeof wpcd.ice_candidate_pool_size === 'number') pcConfig.iceCandidatePoolSize = wpcd.ice_candidate_pool_size;

      const ua = new JsSIP.UA({
        sockets,
        uri: sipUri,
        password: password || undefined,
        authorization_user: authUser,
        display_name: params.from_name || undefined,
        register: true,
        session_timers: false,
        user_agent: 'VentasOS-Softphone/1.0',
        // Cap reconnect attempts so a misconfigured WSS endpoint doesn't
        // spam the console forever.
        connection_recovery_max_interval: 60,
        connection_recovery_min_interval: 5,
      });

      // Track repeated WSS failures and stop after 3 attempts so the
      // user sees a single clear error instead of an infinite loop.
      let wssFailCount = 0;

      ua.on('connecting', () => {
        // eslint-disable-next-line no-console
        console.info('[Softphone] WSS connecting…');
      });

      ua.on('connected', () => {
        // eslint-disable-next-line no-console
        console.info('[Softphone] WSS connected');
      });

      ua.on('disconnected', (e) => {
        // eslint-disable-next-line no-console
        console.warn('[Softphone] WSS disconnected', { code: e?.code, reason: e?.reason, error: e?.error });
        if (e?.error) {
          wssFailCount += 1;
          if (wssFailCount >= 3) {
            try { ua.stop(); } catch { /* */ }
            setError(
              'Telzio WSS endpoint unreachable (1006). Telzio\'s /sip/parameters response is missing the WebSocket path & SIP token — contact Telzio support to enable browser softphone for your account.'
            );
            setPhase('error');
          } else {
            setError(`WSS disconnected: ${e.reason || e.code || 'unknown'}`);
          }
        }
      });

      ua.on('registered', () => {
        // eslint-disable-next-line no-console
        console.info('[Softphone] SIP registered ✓');
        setPhase('registered');
        setError(null);
      });

      ua.on('unregistered', (e) => {
        // eslint-disable-next-line no-console
        console.warn('[Softphone] SIP unregistered', e);
        setPhase('idle');
      });

      ua.on('registrationFailed', (e) => {
        // eslint-disable-next-line no-console
        console.error('[Softphone] Registration failed', { cause: e?.cause, status: e?.response?.status_code, reason: e?.response?.reason_phrase, response: e?.response });
        const msg = e?.response?.status_code
          ? `SIP REGISTER ${e.response.status_code} ${e.response.reason_phrase || ''}`
          : (e?.cause || 'SIP registration failed');
        setError(msg);
        setPhase('error');
      });

      // 15s safety timeout — if neither registered nor failed fires, surface
      // the hang so the user knows to check the console.
      const hangTimer = setTimeout(() => {
        if (uaRef.current === ua && !ua.isRegistered()) {
          // eslint-disable-next-line no-console
          console.error('[Softphone] Registration timed out (15s). Check WSS / token. UA state:', {
            connected: ua.isConnected ? ua.isConnected() : 'n/a',
            registered: ua.isRegistered(),
          });
          setError('Registration timed out — see console');
          setPhase('error');
        }
      }, 15000);
      ua.on('registered', () => clearTimeout(hangTimer));
      ua.on('registrationFailed', () => clearTimeout(hangTimer));

      ua.on('newRTCSession', (data) => {
        const session = data.session;
        if (session.direction === 'incoming') {
          // Don't auto-answer; surface to UI for accept/reject
          setIncomingSession(session);
          setPhase('ringing');
          // Resolve peer info from SIP From header
          const from = session.remote_identity || {};
          const peerLabel = from.display_name || (from.uri && from.uri.user) || 'Unknown';
          const peerE164 = from.uri && from.uri.user ? `+${from.uri.user.replace(/^\+/, '')}` : null;
          // Wire end/fail listeners so the modal closes
          session.on('failed', cleanupSession);
          session.on('ended', cleanupSession);
          session._ventasMeta = { peerLabel, peerE164 };
        }
      });

      uaRef.current = ua;
      ua.start();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Softphone] init failed', e);
      setError(e.message || 'Softphone init failed');
      setPhase('error');
    }
  }, [cleanupSession]);

  const disconnect = useCallback(() => {
    if (sessionRef.current) {
      try { sessionRef.current.terminate(); } catch { /* */ }
    }
    if (uaRef.current) {
      try { uaRef.current.stop(); } catch { /* */ }
      uaRef.current = null;
    }
    cleanupSession();
    setPhase('idle');
    setError(null);
  }, [cleanupSession]);

  const call = useCallback(async (target, label) => {
    if (!uaRef.current) {
      setError('Softphone not connected');
      return;
    }
    const e164 = normalizeE164(target);
    if (!e164) {
      setError(`Invalid number: ${target}`);
      return;
    }
    const targetUri = e164.replace(/^\+/, '');
    const sipUri = uaRef.current.configuration.uri.toString();
    const domain = sipUri.split('@')[1];
    const dialUri = `sip:${targetUri}@${domain}`;

    const session = uaRef.current.call(dialUri, {
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });
    wireSession(session, 'outbound', label || e164, e164);
  }, [wireSession]);

  const answer = useCallback(() => {
    const session = incomingSession;
    if (!session) return;
    const meta = session._ventasMeta || {};
    session.answer({
      mediaConstraints: { audio: true, video: false },
    });
    wireSession(session, 'inbound', meta.peerLabel || 'Caller', meta.peerE164 || null);
    setIncomingSession(null);
  }, [incomingSession, wireSession]);

  const reject = useCallback(() => {
    if (incomingSession) {
      try { incomingSession.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch { /* */ }
    }
    setIncomingSession(null);
    cleanupSession();
  }, [incomingSession, cleanupSession]);

  const hangup = useCallback(() => {
    if (sessionRef.current) {
      try { sessionRef.current.terminate(); } catch { /* */ }
    }
    cleanupSession();
  }, [cleanupSession]);

  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;
    if (muted) {
      sessionRef.current.unmute({ audio: true });
      setMuted(false);
    } else {
      sessionRef.current.mute({ audio: true });
      setMuted(true);
    }
  }, [muted]);

  const sendDtmf = useCallback((digit) => {
    if (!sessionRef.current) return;
    try { sessionRef.current.sendDTMF(String(digit)); } catch { /* */ }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => disconnect(), [disconnect]);

  return {
    phase,
    error,
    currentCall: currentCall ? { ...currentCall, muted } : null,
    incomingSession,
    incomingMeta: incomingSession ? incomingSession._ventasMeta : null,
    connect,
    disconnect,
    call,
    answer,
    reject,
    hangup,
    toggleMute,
    sendDtmf,
  };
}
